/**
 * Asking a publicly shared view for a range of rows.
 *
 * The same shape as the signed-in fetcher and a different endpoint: addressed
 * by slug, carrying no credential at all, and applying the view's filters
 * entirely on the server. The reader is never told what those filters are --
 * `PublicViewSerializer` leaves them out -- so nothing here could send them even
 * if it wanted to.
 */
import { getApi, unwrap } from '@/api/client'
import type { RowData, ViewGroupBy, ViewSort } from '@/api/types'
import type { GroupByMetadata } from '../grid/grouping'
import type { RowFetcher, RowPage } from '../rows/rowFetcher'

interface PublicRowsResponse {
  count?: number | null
  results?: unknown[]
  group_by_metadata?: GroupByMetadata
}

/**
 * How this endpoint is told what the view is grouped and sorted by.
 *
 * The signed-in endpoint reads the view's own group bys and sorts and needs
 * telling nothing. This one is told both, because a public view's ordering is
 * sent by the reader rather than applied from the stored view: left out, the
 * rows come back by id, and any groups are drawn against the wrong order. The
 * backend puts the group by ahead of the sorts itself.
 *
 * The format is the backend's: `-` for a descending term, and the sort type in
 * brackets when it is not the default one.
 */
export function orderParameter(terms: readonly (ViewGroupBy | ViewSort)[]): string {
  return terms
    .map((term) => {
      const direction = term.order === 'DESC' ? '-' : ''
      const type =
        term.type === undefined || term.type === 'default' ? '' : `[${term.type}]`
      return `${direction}field_${term.field}${type}`
    })
    .join(',')
}

/**
 * A fetcher for one slug.
 *
 * A factory rather than a module-level function, because the slug is not in
 * the query: the window's `viewId` carries it, and the endpoint takes it in the
 * path.
 */
function fetchFromApi(slug: string): RowFetcher {
  return async (query, range, signal): Promise<RowPage> => {
    const groupBy = orderParameter(query.groupBys)
    const orderBy = orderParameter(query.sortings)
    const body = await unwrap<PublicRowsResponse>(
      getApi().GET('/api/database/views/grid/{slug}/public/rows/', {
        params: {
          path: { slug },
          query: {
            limit: Math.max(0, range.end - range.start),
            offset: range.start,
            ...(groupBy === '' ? {} : { group_by: groupBy }),
            ...(orderBy === '' ? {} : { order_by: orderBy }),
            ...(query.search.term === ''
              ? {}
              : { search: query.search.term, search_mode: query.search.mode }),
          },
        },
        signal,
      }),
    )

    return {
      rows: (body.results ?? []) as RowData[],
      count: body.count ?? undefined,
      ...(body.group_by_metadata === undefined
        ? {}
        : { groupByMetadata: body.group_by_metadata }),
    }
  }
}

/**
 * The factory every public grid uses, swappable for a stub.
 *
 * The same seam as `__setRowFetcherForTests`, for the same reason: every
 * request this makes carries an abort signal, and jsdom's `AbortController` is
 * not the one Node's `Request` will accept -- so a component test that reached
 * the network would fail on the environment rather than on anything real.
 */
let factory: (slug: string) => RowFetcher = fetchFromApi

export function publicRowFetcher(slug: string): RowFetcher {
  return factory(slug)
}

/** Test seam: hand the public grids a fetcher, or `null` to restore the real one. */
export function __setPublicRowFetcherForTests(
  replacement: ((slug: string) => RowFetcher) | null,
): void {
  factory = replacement ?? fetchFromApi
}
